import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  TextField,
  Stack,
  Alert,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import EditIcon from "@mui/icons-material/Edit";
import ScannerNew from "../../../../components/common/ScannerNew";
import { LookupInventoryCode } from "../../../services/InventoryTrackerService";

const STEP = { CHOOSE: "choose", SCAN: "scan", MANUAL: "manual" };

/**
 * ENB-584: same Scan / Enter Manually choice as the temperature tracker. The code is looked up
 * as a product item number first, then a location barcode. A code that matches nothing shows an
 * alert with the option to add it as a new product.
 */
const InventoryScanDialog = ({ open, onClose, onFound, onAddProduct }) => {
  const [step, setStep] = useState(STEP.CHOOSE);
  const [manualText, setManualText] = useState("");
  const [looking, setLooking] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [notFoundCode, setNotFoundCode] = useState("");

  const reset = () => {
    setStep(STEP.CHOOSE);
    setManualText("");
    setLooking(false);
    setErrorMsg("");
    setNotFoundCode("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const lookup = async (code) => {
    const text = (code || "").trim();
    if (!text) return;

    setLooking(true);
    setErrorMsg("");
    setNotFoundCode("");
    try {
      const response = await LookupInventoryCode(text);
      const result = response?.results;

      if (!result || result.matchType === "NotFound") {
        setNotFoundCode(text);
        setLooking(false);
        return;
      }

      reset();
      onFound(result);
    } catch {
      setErrorMsg("Error looking up the code. Please try again.");
      setLooking(false);
    }
  };

  const handleScan = (scannedText) => {
    if (looking || notFoundCode) return;
    lookup(scannedText);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Scan Inventory</DialogTitle>

      <DialogContent>
        {step === STEP.CHOOSE && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Scan a product label or a shelf / rack label.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<QrCodeScannerIcon />}
              onClick={() => setStep(STEP.SCAN)}
              sx={{ justifyContent: "flex-start", py: 2 }}
            >
              Scan Barcode
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<EditIcon />}
              onClick={() => setStep(STEP.MANUAL)}
              sx={{ justifyContent: "flex-start", py: 2 }}
            >
              Enter Manually
            </Button>
          </Stack>
        )}

        {step === STEP.SCAN &&
          (looking ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
              <CircularProgress size={24} />
              <Typography>Looking up...</Typography>
            </Box>
          ) : (
            !notFoundCode && <ScannerNew onScan={handleScan} />
          ))}

        {step === STEP.MANUAL && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Item Number or Location Barcode"
              value={manualText}
              onChange={(e) => {
                setManualText(e.target.value);
                setNotFoundCode("");
              }}
              onKeyDown={(e) => e.key === "Enter" && lookup(manualText)}
              fullWidth
              size="small"
              autoFocus
              disabled={looking}
            />
          </Stack>
        )}

        {notFoundCode && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => onAddProduct(notFoundCode)}>
                Add Product
              </Button>
            }
          >
            "{notFoundCode}" is not in the system.
          </Alert>
        )}

        {errorMsg && (
          <Typography color="error" sx={{ mt: 2 }} variant="body2">
            {errorMsg}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {step !== STEP.CHOOSE && (
          <Button onClick={reset} disabled={looking}>
            Back
          </Button>
        )}
        {step === STEP.SCAN && notFoundCode && (
          <Button variant="contained" onClick={() => setNotFoundCode("")}>
            Scan Again
          </Button>
        )}
        {step === STEP.MANUAL && (
          <Button variant="contained" onClick={() => lookup(manualText)} disabled={looking || !manualText.trim()}>
            Look Up
          </Button>
        )}
        <Button onClick={handleClose} disabled={looking} variant="outlined" color="secondary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryScanDialog;
